import React, { useMemo, useState } from 'react'
import Button from '../ui/Button'
import {
  applyFloorSheetCopy,
  copySectionsForTab,
  type FloorSheetCopyPanelWeld,
  weldCopyLabel
} from '../../utils/floorSheetCopy'

type FloorSheetCopyPanelProps = {
  activeTab: string
  sourceWeld: FloorSheetCopyPanelWeld
  sourceWeldIndex: number
  sourcePassLabels: string[]
  welds: FloorSheetCopyPanelWeld[]
  busy?: boolean
  embedded?: boolean
  onApply: (nextWelds: FloorSheetCopyPanelWeld[]) => void | Promise<void>
}

export default function FloorSheetCopyPanel({
  activeTab,
  sourceWeld,
  sourcePassLabels,
  welds,
  busy = false,
  embedded = false,
  onApply
}: FloorSheetCopyPanelProps) {
  const [open, setOpen] = useState(false)
  const [copyTargets, setCopyTargets] = useState<string[]>([])
  const sectionOptions = useMemo(
    () => copySectionsForTab(activeTab, sourcePassLabels),
    [activeTab, sourcePassLabels]
  )
  const [copySections, setCopySections] = useState<string[]>(sectionOptions)

  React.useEffect(() => {
    setCopySections(sectionOptions)
    setCopyTargets([])
    setOpen(false)
  }, [activeTab, sectionOptions])

  const targetWelds = welds.filter((w) => w.id !== sourceWeld.id)

  const handleApply = () => {
    if (copyTargets.length === 0 || copySections.length === 0 || busy) return
    const selected = new Set(copySections)
    const nextWelds = welds.map((weld) => {
      if (!copyTargets.includes(weld.id)) return weld
      const merged = applyFloorSheetCopy(sourceWeld, weld, activeTab, selected, sourcePassLabels)
      return { ...weld, ...merged }
    })
    void Promise.resolve(onApply(nextWelds)).then(() => setOpen(false))
  }

  if (targetWelds.length === 0) return null

  const copyButton = (
    <button
      type="button"
      className="rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-medium text-heritage-grey shadow-sm transition hover:border-[#D0D5DD] hover:text-blueprint-blue h-7"
      onClick={() => setOpen(true)}
    >
      Copy to…
    </button>
  )

  const copyPanel = (
    <div className="rounded-lg border border-[#E2E5EA] bg-white px-4 py-3 text-xs text-heritage-grey shadow-sm">
      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <p className="text-[11px] font-semibold text-heritage-grey">Copy to welds</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {targetWelds.map((weld, index) => (
              <label
                key={`copy-target-${weld.id}`}
                className="flex items-center gap-2 rounded-md border border-[#E2E5EA] px-2 py-1"
              >
                <input
                  type="checkbox"
                  checked={copyTargets.includes(weld.id)}
                  onChange={(event) => {
                    setCopyTargets((prev) =>
                      event.target.checked
                        ? [...prev, weld.id]
                        : prev.filter((id) => id !== weld.id)
                    )
                  }}
                  className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                />
                <span>{weldCopyLabel(weld, welds.indexOf(weld))}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-heritage-grey">Sections to copy</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {sectionOptions.map((section) => (
              <label
                key={`copy-section-${section}`}
                className="flex items-center gap-2 rounded-md border border-[#E2E5EA] px-2 py-1"
              >
                <input
                  type="checkbox"
                  checked={copySections.includes(section)}
                  onChange={(event) => {
                    setCopySections((prev) =>
                      event.target.checked
                        ? [...prev, section]
                        : prev.filter((item) => item !== section)
                    )
                  }}
                  className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                />
                <span>{section}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          type="button"
          loading={busy}
          disabled={busy || copyTargets.length === 0 || copySections.length === 0}
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
  )

  if (embedded) {
    if (open) {
      return (
        <div className="order-last w-full basis-full">
          {copyPanel}
        </div>
      )
    }
    return copyButton
  }

  return (
    <div className="border-t border-[#E2E5EA] px-1 py-2">
      {!open ? <div className="flex justify-end">{copyButton}</div> : copyPanel}
    </div>
  )
}
